import { useState } from "react";
import { Minus, Plus, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { api, errMsg, money } from "@/lib/api";

export function CartDrawer() {
  const { items, setQty, total, open, setOpen } = useCart();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
        email: email || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col border-white/10 bg-[#0b111c] sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Your order</SheetTitle>
          <SheetDescription className="text-slate-400">Hardware and setup fees. Processing rates are quoted after underwriting.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 flex-1 space-y-4 overflow-y-auto" data-lenis-prevent>
          {items.length === 0 && <p className="text-sm text-slate-400" data-testid="cart-empty">Your cart is empty.</p>}
          {items.map((i) => (
            <div key={i.id} className="flex gap-4 border-b border-white/10 pb-4" data-testid={`cart-item-${i.id}`}>
              <img src={i.image} alt={i.name} className="h-16 w-16 object-cover clip-notch" />
              <div className="flex-1">
                <p className="font-display font-medium">{i.name}</p>
                <p className="font-mono text-xs text-slate-400">{money(i.price)} each</p>
                <div className="mt-2 flex items-center gap-2">
                  <button onClick={() => setQty(i.id, i.qty - 1)} data-testid={`cart-dec-${i.id}`} className="grid h-7 w-7 place-items-center border border-white/15 hover:border-sky-400"><Minus className="h-3 w-3" /></button>
                  <span className="w-6 text-center font-mono text-sm" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                  <button onClick={() => setQty(i.id, i.qty + 1)} data-testid={`cart-inc-${i.id}`} className="grid h-7 w-7 place-items-center border border-white/15 hover:border-sky-400"><Plus className="h-3 w-3" /></button>
                </div>
              </div>
              <p className="font-mono text-sm">{money(i.price * i.qty)}</p>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="space-y-4 border-t border-white/10 pt-5">
            <input className="field" type="email" placeholder="Email for receipt (optional)" value={email}
              onChange={(e) => setEmail(e.target.value)} data-testid="cart-email-input" />
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-slate-400">Subtotal (tax at checkout)</span>
              <span className="font-display text-2xl font-semibold" data-testid="cart-total">{money(total)}</span>
            </div>
            <button onClick={checkout} disabled={busy} data-testid="cart-checkout-button" className="btn-primary w-full justify-center">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Secure checkout
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
